import { z } from "zod";
import { isValidCategory } from "@/utils/category-mapping";
import { PLACES_CATEGORIES } from "@/utilities/constants/PlacesCategories";

// form zod validation schema
export const createPlaceSchema = z.object({
  name: z.string().min(1, "Name is required"),
  category: z.string()
    .optional()
    .refine(
      (value) => !value || PLACES_CATEGORIES.includes(value),
      `Category must be one of the predefined values. Use empty value or select from the available categories.`
    ),
  latitude: z
    .string()
    .min(1, "Latitude is required")
    .refine((value) => !isNaN(Number(value)), "Latitude must be a valid number")
    .refine((value) => {
      const num = Number(value);
      return num >= -90 && num <= 90;
    }, "Latitude must be between -90 and 90"),
  longitude: z
    .string()
    .min(1, "Longitude is required")
    .refine((value) => !isNaN(Number(value)), "Longitude must be a valid number")
    .refine((value) => {
      const num = Number(value);
      return num >= -180 && num <= 180;
    }, "Longitude must be between -180 and 180"),
});

// Schema for CSV import validation (same as create but with specific error messages for CSV context)
export const importPlaceSchema = z.object({
  name: z.string().min(1, "Name is required"),
  category: z.string()
    .optional()
    .refine(
      (value) => !value || isValidCategory(value),
      "Invalid category. Please use a valid category from the predefined list or leave empty."
    ),
  latitude: z
    .string()
    .min(1, "Latitude is required")
    .refine((value) => !isNaN(Number(value)), "Latitude must be a valid number")
    .refine((value) => {
      const num = Number(value);
      return num >= -90 && num <= 90;
    }, "Latitude must be between -90 and 90"),
  longitude: z
    .string()
    .min(1, "Longitude is required")
    .refine((value) => !isNaN(Number(value)), "Longitude must be a valid number")
    .refine((value) => {
      const num = Number(value);
      return num >= -180 && num <= 180;
    }, "Longitude must be between -180 and 180"),
});

// generate form types from zod validation schema
export type CreatePlaceInput = z.infer<typeof createPlaceSchema>;
